import { FormattedMessage } from 'react-intl';
import { useContext, useEffect } from 'react';
import { NavigationType, useBlocker, useLocation, useParams } from 'react-router';

// MUI
import Typography from '@mui/material/Typography';

// common
import { PanelDataContext } from 'modules/context';
import TabsView from 'common/components/TabsView';

// screen
import { PanelDataProvider } from 'modules/suppliers/detail/context/dataContext';
import SupplierRefCards from 'modules/suppliers/detail/components/SupplierRefCards';
import { useTraceabilityBack } from 'modules/suppliers/detail/hooks/useTraceabilityBack';
import GeneralDetailsPanel from 'modules/suppliers/detail/components/tab-panels/GeneralDetails';
import GoodsTypePanel from 'modules/suppliers/detail/components/tab-panels/GoodsType';
import QuickSearchDetail from 'modules/quick-search/components/QuickSearchDetail';
import BusinessRegistrationCertificate from 'modules/suppliers/detail/components/tab-panels/BusinessRegistrationCertificate';
import SupplyContract from 'modules/suppliers/detail/components/tab-panels/SupplyContract';
import CertificateOfFoodSafety from 'modules/suppliers/detail/components/tab-panels/CertificateOfFoodSafety';
import OtherDocuments from 'modules/suppliers/detail/components/tab-panels/OtherDocuments';

/**
 * Chỉ xem được thông tin của các nhà cung cấp có trang thái là "Đang cung cấp",
 * nếu "Tạm dùng" thì sẽ không được xem
 */
const SupplierDetailsContent = () => {
  const context = useContext(PanelDataContext);
  const location = useLocation();
  const params = useParams<{ id?: string }>();
  // Vào từ Quick Search → chỉ xem Thông tin chung + Loại hàng hóa (lược bớt dữ liệu).
  // Nhận diện qua route param :id (route quick-search/:id/view-only) — bền cả khi refresh
  // (state điều hướng có thể mất), kèm fallback location.state.from cho tương thích.
  const isFromQuickSearch =
    !!params.id || (location.state as { from?: string } | null)?.from === 'quickSearch';
  const { fromFoodTraceability, goBackToTraceability } = useTraceabilityBack();

  // Cho nút back của trình duyệt hoạt động giống nút "Về truy xuất": chặn thao
  // tác back (POP) khi đang xem hồ sơ NCC từ chức năng Truy xuất thực phẩm, rồi
  // điều hướng về Map kèm state để mở lại dialog truy xuất. Dùng replace để
  // không để lại entry hồ sơ NCC kẹt giữa lịch sử.
  const blocker = useBlocker(
    ({ historyAction }) => fromFoodTraceability && historyAction === NavigationType.Pop
  );

  useEffect(() => {
    if (blocker.state === 'blocked') {
      blocker.reset();
      goBackToTraceability({ replace: true });
    }
  }, [blocker, goBackToTraceability]);

  if (!context) {
    throw new Error('SupplierDetailsContent must be used within PanelDataProvider');
  }

  // Danh sách tab đầy đủ (luồng /suppliers). Quick Search chỉ lấy 2 tab đầu.
  const allTabs: { label: string; content: JSX.Element }[] = [
    {
      label: 'supplier.tabs.generalDetails',
      // Quick Search dùng màn chi tiết riêng (2 card tham chiếu + form theo design), khác QR NCC TPAT.
      content: isFromQuickSearch ? <QuickSearchDetail /> : <GeneralDetailsPanel />,
    },
    { label: 'supplier.tabs.goodsTypes', content: <GoodsTypePanel /> },
    { label: 'supplier.tabs.businessRegistration', content: <BusinessRegistrationCertificate /> },
    { label: 'supplier.tabs.supplyContract', content: <SupplyContract /> },
    { label: 'supplier.tabs.foodSafetyCertification', content: <CertificateOfFoodSafety /> },
    { label: 'supplier.tabs.otherDocuments', content: <OtherDocuments /> },
  ];
  const tabs = isFromQuickSearch ? allTabs.slice(0, 2) : allTabs;

  return (
    <>
      {/* Quick Search (toàn hệ thống) không có "liên kết theo trường" nên ẩn dòng trạng thái liên kết. */}
      {!isFromQuickSearch && (
        <Typography sx={{ textAlign: 'right' }}>
          <FormattedMessage id="supplier.associationStatus" />
          {': '}✅
          <FormattedMessage id={`supplier.associationStatuses.providing`} />
        </Typography>
      )}
      {/*
        "Thông tin NCC tham chiếu" + "Thông tin cơ bản HSNL" đứng NGOÀI TabsView theo thiết kế
        (node 6885-26427 quick search / 7223-26633 QR NCC TPAT): đổi sang tab "Loại hàng hoá" vẫn
        thấy đang xem hồ sơ của ai.
      */}
      <SupplierRefCards />
      <TabsView tabLabels={tabs.map((t) => t.label)} tabContents={tabs.map((t) => t.content)} />
    </>
  );
};

const SupplierDetailPage = () => {
  return (
    <PanelDataProvider>
      <SupplierDetailsContent />
    </PanelDataProvider>
  );
};

export default SupplierDetailPage;
